/**
 * BYO v2 — "Use Existing Function" selection for the edit modal (split out of
 * {@link useByoEditSource}).
 *
 * When the user picks a processor from "Use Existing Function", fetch it so
 * its code can be PREVIEWED and test-run. The save path never needs its code:
 * a predefined save references the picked container by id
 * (ensureCodeContainerId's direct-reference short-circuit).
 *
 * NEX-22636: this used to copy the picked code/packages into `code` /
 * `packages` — the user's OWN-code draft — so switching back to "Provide
 * Your Own Function" showed (and a save then wrote) the picked function's
 * code into the user's private container. The pick now lives only in the
 * separate `predefinedPreview` fields returned here.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getCodeContainer } from 'js/actions';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { decodeCodeContainerContent } from 'new/components/flows/types/byo/core/services';
import { selectCodeContainersById } from 'new/state/entities/selectors/transformSelectors';

export interface UseByoProcessorPickerArgs {
  /** The picked container id (owned by useByoCodeContainerHydration). */
  predefinedId: number | string | null;
  /** Cleared on a failed fetch so the user can pick again. */
  setPredefinedId: (id: number | string | null) => void;
}

export interface ByoPredefinedPreview {
  code: string;
  packages: string[];
  extraData: string;
}

export interface UseByoProcessorPicker {
  /** A pick has been made but its container hasn't been fetched yet — Save must be blocked. */
  processorPickPending: boolean;
  /** Fetching the picked container failed (the pick is cleared). */
  pickError: string | null;
  /** The picked function's own code / packages / extra_data, for preview + test runs only. */
  predefinedPreview: ByoPredefinedPreview;
}

export function useByoProcessorPicker({
  predefinedId,
  setPredefinedId,
}: UseByoProcessorPickerArgs): UseByoProcessorPicker {
  const dispatch = useAppDispatch();
  const transforms = useSelector(selectCodeContainersById);

  const [loadedPickId, setLoadedPickId] = useState<number | string | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  useEffect(() => {
    if (predefinedId == null) return;
    let cancelled = false;
    setPickError(null);
    // NEX-22636: a failed fetch used to be an unhandled rejection, leaving
    // processorPickPending true forever (Save blocked, no message). Surface it
    // and clear the pick so the user can choose again (same id included).
    Promise.resolve(dispatch(getCodeContainer(predefinedId as any) as any))
      .then(() => {
        if (!cancelled) setLoadedPickId(predefinedId);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setPickError(
          `Couldn't load the selected function${e?.message ? ` (${e.message})` : ''}. Pick it again or choose another.`,
        );
        setPredefinedId(null);
      });
    return () => {
      cancelled = true;
    };
    // setPredefinedId is a useState setter (stable) — same deps as before the split.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predefinedId, dispatch]);

  const selectedProcessorInStore = predefinedId != null ? transforms[predefinedId] : null;
  // A pick has been made but its container hasn't been fetched yet — Save must
  // be blocked for this exact window, see the `processorPickPending` doc on
  // UseByoEditSource.
  const processorPickPending = predefinedId != null && loadedPickId !== predefinedId;
  const predefinedPreview = useMemo(
    () => ({
      ...decodeCodeContainerContent(processorPickPending ? null : selectedProcessorInStore),
      extraData: (!processorPickPending && selectedProcessorInStore?.code_config?.extra_data) || '',
    }),
    [processorPickPending, selectedProcessorInStore],
  );

  return { processorPickPending, pickError, predefinedPreview };
}

export default useByoProcessorPicker;
